'use client';

import { Badge } from '@ophelos/ui/components/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@ophelos/ui/components/card';
import { ArrowDownRight, ArrowUpRight, CheckCircle } from 'lucide-react';
import { CurrencyDisplay } from '@/components/ui/CurrencyDisplay';
import { useTranslation } from '@/hooks/useTranslation';
import { fromMillicents } from '@/lib/currency';
import type { DebtData } from '@/types';
import type { TransactionItem } from '@/types/core-api';
import { formatLedgerDate, parseChargeDate } from './charges';

/** Transaction types that are relevant to the customer (payments they made + refunds they received) */
const CUSTOMER_VISIBLE_TYPES = new Set([
  'payment',
  'bank_payment',
  'client_payment',
  'client_payment_reversal',
  'payment_failed',
  'refund',
]);

export function transactionLowersBalance(tx: TransactionItem): boolean {
  return tx.type !== 'client_payment_reversal' && tx.direction === 'credit';
}

export function transactionShowsPlusSign(tx: TransactionItem): boolean {
  return tx.type === 'client_payment_reversal';
}

/** Filter transactions to only those relevant to the customer */
export function filterCustomerTransactions(transactions: TransactionItem[]): TransactionItem[] {
  return transactions.filter((tx) => CUSTOMER_VISIBLE_TYPES.has(tx.type ?? ''));
}

/**
 * The same list, across every case the account holds — cleared ones included, since each case's
 * own history is only part of the story.
 */
export function customerTransactions(debts: DebtData[]): TransactionItem[] {
  return filterCustomerTransactions(debts.flatMap((debt) => debt.transactions ?? []));
}

/** Newest first. The ledger returns no guaranteed order, and ties are broken by id so the list is stable across renders. */
export function newestFirst(transactions: TransactionItem[]): TransactionItem[] {
  const timeOf = (tx: TransactionItem) => parseChargeDate(tx.created_at)?.getTime() ?? 0;
  return [...transactions].sort((a, b) => {
    const byDate = timeOf(b) - timeOf(a);
    return byDate || a.transaction_id.localeCompare(b.transaction_id);
  });
}

/** Title as an i18n key + values. `payment_plan.index` is 0-based, so instalments read one higher. */
export function transactionTitle(tx: TransactionItem): [key: string, values?: { number: number }] {
  if (tx.type === 'refund') return ['transaction.refund'];
  if (tx.type === 'client_payment_reversal') return ['transaction.paymentReversal'];
  const instalment = (tx.metadata?.payment_plan as { index?: number } | undefined)?.index;
  return typeof instalment === 'number' ? ['transaction.planInstalment', { number: instalment + 1 }] : ['transaction.payment'];
}

interface TransactionRowProps {
  title: string;
  date: string;
  /** Falsy drops the segment rather than rendering an empty bullet. */
  method?: string;
  amount: number;
  /** Money in: rendered negative and in the success tone, matching the ledger's credit direction. */
  lowersBalance: boolean;
  /** Reversal of a client payment: rendered positive (restores balance). */
  showPlus?: boolean;
  currency: string;
  appliedTo?: string;
  /** The ledger's own rows are settled; a historical record makes no such claim. */
  showCompleted?: boolean;
}

/**
 * One row of the payment timeline. Shared so a migrated case's pre-Forest history renders
 * identically to the ledger's own transactions — same icon, spacing and amount treatment —
 * without the two lists drifting apart.
 */
export function TransactionRow({
  title,
  date,
  method,
  amount,
  lowersBalance,
  showPlus = false,
  currency,
  appliedTo,
  showCompleted = true,
}: TransactionRowProps) {
  const { t } = useTranslation();
  const DirectionIcon = lowersBalance ? ArrowDownRight : ArrowUpRight;

  return (
    <div className="flex items-start py-3 px-3 justify-between gap-4 w-full">
      <div className="flex gap-3 flex-1">
        <div className={`size-8 rounded-full flex items-center justify-center shrink-0 ${lowersBalance ? 'highlight-green' : 'bg-soft'}`}>
          <DirectionIcon className="size-4" />
        </div>
        <div className="flex-1 min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h5 className="font-medium text-sm whitespace-normal text-left">{title}</h5>
            {showCompleted && (
              <Badge variant="outline">
                <CheckCircle className="size-3.5 text-green-600" />
                {t('status.completed')}
              </Badge>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-2 text-sm text-muted-foreground">
            <span className="whitespace-nowrap">{date}</span>
            {method && (
              <>
                <span>•</span>
                <span className="whitespace-nowrap">{method}</span>
              </>
            )}
          </div>
          {appliedTo && (
            <div className="text-sm text-muted-foreground text-left">
              {t('transaction.appliedTo')} <span className="font-medium">{appliedTo}</span>
            </div>
          )}
        </div>
      </div>
      <div className={`inline-flex gap-2 items-center ${lowersBalance ? 'text-green-600' : showPlus ? 'text-amber-600' : ''}`}>
        <span className="text-base">{lowersBalance ? '-' : showPlus ? '+' : ''}</span>
        <CurrencyDisplay amount={amount} currency={currency} className="font-semibold text-base" />
      </div>
    </div>
  );
}

interface TransactionsListProps {
  transactions: TransactionItem[];
  title?: string;
  emptyMessage?: string;
  currency?: string;
  /** From {@link debtNameByTransaction} — set only for a list spanning several debts. */
  debtNames?: Map<string, string>;
}

export function TransactionsList({ transactions, title, emptyMessage, currency = 'EUR', debtNames }: TransactionsListProps) {
  const { t, currentLanguage } = useTranslation();
  const displayTitle = title || t('transaction.transactions');
  const displayEmptyMessage = emptyMessage || t('transaction.noPaymentHistory');

  if (transactions.length === 0 && displayEmptyMessage) {
    return (
      <div className="flex-1 px-8 py-6 overflow-y-auto space-y-2">
        <h4 className="font-semibold text-base">{displayTitle}</h4>
        <div className="text-center py-12 text-muted-foreground">
          <p>{displayEmptyMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 stack min-h-0 max-w-xl mx-auto w-full">
      <Card variant="fullscreen" className="pb-2">
        <CardHeader>
          <CardTitle>{displayTitle}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 px-2">
          {newestFirst(transactions).map((tx) => {
            const [titleKey, titleValues] = transactionTitle(tx);

            return (
              <TransactionRow
                key={tx.transaction_id}
                title={t(titleKey, titleValues)}
                date={formatLedgerDate(tx.created_at, currentLanguage)}
                // An unmapped method resolves to '' and drops out, rather than showing a raw value.
                method={t(`paymentMethod.${tx.metadata?.payment_method}`, { defaultValue: '' })}
                amount={fromMillicents(tx.amount_millicents)}
                lowersBalance={transactionLowersBalance(tx)}
                showPlus={transactionShowsPlusSign(tx)}
                currency={currency}
                appliedTo={debtNames?.get(tx.transaction_id)}
              />
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}
